import React from "react"
import { Loader2, LogOut, RotateCw } from "lucide-react"

import { Button } from "@/components/ui/button"

type BillingServiceUnavailableProps = {
  isRetrying: boolean
  onLogout: () => void
  onRetry: () => void
}

// Full-screen interstitial for when the platform cannot determine the
// merchant's access at all (the billing backbone is unreachable). The user
// never sees a broken "Choose a Plan" dialog or technical wording — just a
// calm "temporary hiccup, we're on it" state with a retry. Deliberately
// self-contained: no toasts, no dialogs, nothing else competes with it.
export const BillingServiceUnavailable: React.FC<BillingServiceUnavailableProps> = ({
  isRetrying,
  onLogout,
  onRetry,
}) => {
  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center overflow-hidden bg-background"
      role="status"
      aria-live="polite"
      data-testid="billing-unavailable-screen"
    >
      {/* Atmosphere: faint dot grid fading out from the center + a warm glow
          from the top. Quiet, not alarming — this is a waiting room, not an
          error page. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 [background-image:radial-gradient(circle,rgba(10,10,10,0.05)_1px,transparent_1px)] [background-size:22px_22px] [mask-image:radial-gradient(ellipse_60%_55%_at_50%_45%,black_35%,transparent_100%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[24rem] bg-[radial-gradient(48rem_20rem_at_50%_-4rem,rgba(251,191,36,0.16),transparent_70%)]"
      />

      <div className="relative mx-auto flex w-full max-w-md flex-col items-center gap-7 px-6 text-center">
        <div className="animate-in fade-in-0 slide-in-from-bottom-3 fill-mode-both flex items-center gap-2.5 rounded-full border border-amber-200 bg-amber-50 py-1.5 pr-4 pl-3 duration-700">
          <span className="relative flex size-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-60" />
            <span className="relative inline-flex size-2.5 rounded-full bg-amber-500" />
          </span>
          <span className="text-xs font-semibold tracking-[0.14em] text-amber-800 uppercase">
            Temporary hiccup
          </span>
        </div>

        <div className="animate-in fade-in-0 slide-in-from-bottom-3 fill-mode-both flex flex-col gap-3 duration-700 [animation-delay:120ms]">
          <h1 className="text-4xl font-semibold tracking-tight text-balance text-foreground sm:text-5xl">
            We&rsquo;ll be right back
          </h1>
          <p className="text-base leading-7 text-pretty text-muted-foreground">
            Something isn&rsquo;t working on our side and we&rsquo;re already fixing it. Please
            check back in a few minutes.
          </p>
        </div>

        <div className="animate-in fade-in-0 slide-in-from-bottom-3 fill-mode-both flex items-center gap-3 duration-700 [animation-delay:240ms]">
          <Button
            type="button"
            size="lg"
            onClick={onRetry}
            disabled={isRetrying}
            data-testid="billing-unavailable-retry"
          >
            {isRetrying ? (
              <Loader2 data-icon="inline-start" className="animate-spin" />
            ) : (
              <RotateCw data-icon="inline-start" />
            )}
            {isRetrying ? "Checking…" : "Try again"}
          </Button>
          <Button type="button" size="lg" variant="ghost" onClick={onLogout}>
            <LogOut data-icon="inline-start" />
            Log out
          </Button>
        </div>

        <p className="animate-in fade-in-0 fill-mode-both text-xs text-muted-foreground/80 duration-700 [animation-delay:400ms]">
          Your products and settings are safe — nothing was lost.
        </p>
      </div>
    </div>
  )
}
